import React, { createContext, useContext, useState, useEffect } from 'react';
import { superAdminApiService } from '../services/SuperAdminApiService';

interface SuperAdmin {
  id: string;
  username: string;
  authenticated: boolean;
}

interface SuperAdminAuthContextType {
  superAdmin: SuperAdmin | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const SuperAdminAuthContext = createContext<SuperAdminAuthContextType | undefined>(undefined);

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext);
  if (context === undefined) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
};

interface SuperAdminAuthProviderProps {
  children: React.ReactNode;
}

export const SuperAdminAuthProvider: React.FC<SuperAdminAuthProviderProps> = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState<SuperAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verifica se c'è una sessione attiva
    const savedAuth = localStorage.getItem('superadmin_auth');
    if (savedAuth) {
      try {
        const parsed = JSON.parse(savedAuth);
        setSuperAdmin(parsed);
      } catch (error) {
        localStorage.removeItem('superadmin_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await superAdminApiService.login(username, password);
      
      localStorage.setItem('superadmin_token', response.token);
      localStorage.setItem('superadmin_auth', JSON.stringify({
        id: response.id,
        username: response.username,
        authenticated: true
      }));
      
      setSuperAdmin({
        id: response.id,
        username: response.username,
        authenticated: true
      });
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };  const logout = () => {
    setSuperAdmin(null);
    localStorage.removeItem('superadmin_auth');
    localStorage.removeItem('superadmin_token');
  };

  const value: SuperAdminAuthContextType = {
    superAdmin,
    login,
    logout,
    isAuthenticated: !!superAdmin?.authenticated,
    loading,
  };

  return (
    <SuperAdminAuthContext.Provider value={value}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};
